import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react'
import PropTypes from 'prop-types'
import { FormElement } from '~src/new/shared/ui/forms/FormElement/FormElement'

import cls from './form-elements-group.module.scss'

export const FormElementsGroup = forwardRef(
  (
    {
      elements = [],
      values = {},
      size = '',
      onChangeField,
      onFieldFocus,
      externalErrors: initialExternalErrors,
    },
    ref,
  ) => {
    const [externalErrors, setExternalErrors] = useState(initialExternalErrors)
    const [useExternalErrors, setUseExternalErrors] = useState(false)

    const elementRefs = useRef({})

    const updateElementsDisabledState = useCallback(() => {
      const nElements = !Array.isArray(elements) ? [] : [...elements]

      return nElements.reduce((a, { id, disabledIf, disabled: elemDisabled }) => {
        if (elemDisabled !== undefined) {
          a[id] = true
          return a
        }

        let disabled = false

        if (disabledIf) {
          let invert = false
          let disabledIfValue = disabledIf
          if (disabledIf.startsWith('!')) {
            invert = true
            disabledIfValue = disabledIf.slice(1)
          }

          let viRefElem = elementRefs.current[disabledIfValue]

          let value = viRefElem ? !!viRefElem.getValue() : !!values[id]
          if (invert) value = !value

          disabled = value

          a[id] = disabled
        }

        return a
      }, {})
    }, [elements, values])

    const [disabledElems, setDisabledElems] = useState(updateElementsDisabledState())

    useEffect(() => {
      if (initialExternalErrors !== undefined) {
        setExternalErrors(initialExternalErrors)
        setUseExternalErrors(true)
      }
    }, [initialExternalErrors])

    const forEachRefElement = useCallback(
      method => {
        for (const element of elements) {
          if (typeof element === 'string' || element.hidden === true) {
            continue
          }
          if (method(elementRefs.current[element.id], element.id)) {
            return
          }
        }
      },
      [elements],
    )

    const getValues = useCallback(() => {
      const result = {}

      forEachRefElement((refElement, id) => {
        let value = refElement ? refElement.getValue() : undefined
        if (value !== undefined) {
          result[id] = value
        }
      })

      return result
    }, [elements])

    const checkValidity = useCallback(() => {
      let isValid = true

      forEachRefElement(refElement => {
        if (refElement && !refElement.hidden && !refElement.checkValidity()) {
          isValid = false
        }
      })

      return isValid
    }, [elements])

    useImperativeHandle(ref, () => {
      return {
        getValues,
        checkValidity,
        getFiles: () => {
          const files = []
          const forEachRefElement = method => {
            for (const element of elements) {
              if (typeof element === 'string' || element.hidden === true) {
                continue
              }
              if (method(elementRefs.current[element.id], element.id)) {
                return
              }
            }
          }

          forEachRefElement((refElement, id) => {
            if (refElement && refElement.getFiles) {
              const elementFiles = refElement.getFiles()
              if (elementFiles && elementFiles.length > 0) {
                files.push(...elementFiles)
              }
            }
          })
          return files
        },
      }
    }, [getValues, checkValidity, elements])

    const handleFieldFocus = useCallback(
      (...params) => {
        setUseExternalErrors(false)
        onFieldFocus && onFieldFocus(...params)
      },
      [onFieldFocus],
    )

    const onCheckboxChange = useCallback(() => {
      setDisabledElems(updateElementsDisabledState())
    }, [elements, values])

    const onChange = useCallback(
      (element, event) => {
        if (element.type.toLowerCase() === 'checkbox') {
          onCheckboxChange()
        } else {
          onChangeField && onChangeField(element.id, event)
        }
      },
      [onChangeField, onCheckboxChange],
    )

    const nElements = !Array.isArray(elements) ? [] : elements
    const currentExternalErrors = useExternalErrors ? externalErrors : {}

    return nElements.map((element, index) => {
      // TODO remove after the backend return the correct data
      if (element.type === 'boolean' && typeof element.value === 'string') {
        element.value = element.value === 'Yes'
      }

      if (element.hidden === true) return null

      return (
        <FormElement
          size={size}
          key={element.id || index}
          externalError={element.id && currentExternalErrors[element.id]}
          inputRef={ref => (elementRefs.current[element.id] = ref)}
          value={values[element.id]}
          hideButtonId={element.hideButtonId}
          data={element}
          onFieldFocus={handleFieldFocus}
          onChange={e => onChange(element, e)}
          disabled={disabledElems[element.id]}
        />
      )
    })
  },
)

FormElementsGroup.propTypes = {
  elements: PropTypes.array.isRequired,
  values: PropTypes.object,
  size: PropTypes.string,
  onChangeField: PropTypes.func,
  onFieldFocus: PropTypes.func,
  externalErrors: PropTypes.object,
}
